/**
 * The buyer flow as a pure state machine over spec §7 steps 1–5 with the
 * named failure arrows. Explicit transition table, repo convention: illegal
 * transitions throw (walls, not signs) — with two deliberate exceptions
 * required by §7's own race semantics: CONFIRMED is absorbing (a late
 * failure event can never un-confirm a purchase), and repeated
 * CHECKOUT_STARTED taps collapse onto the FIRST client key (Inv 4: a
 * double-tapped button creates one order).
 */

export type FlowState =
  | { readonly phase: "BROWSE" }
  | { readonly phase: "CART"; readonly cartId: string }
  | { readonly phase: "CHECKOUT_IN_FLIGHT"; readonly cartId: string; readonly checkoutKey: string }
  | {
      readonly phase: "PENDING_PAYMENT";
      readonly cartId: string;
      readonly checkoutKey: string;
      readonly orderId: string;
      readonly holdExpiresAt: number;
      /**
       * The provider confirmed the charge (confirmPayment resolved ok) but
       * the kernel has not: §7 step 5 arrives via the webhook (B23b). An
       * authorized order never expires in the UI — B29's sweeper defers
       * expiry for a SUCCEEDED intent, so the clock arrow is the server's
       * stance mirrored, not an optimism.
       */
      readonly authorized: boolean;
    }
  | { readonly phase: "CONFIRMED"; readonly orderId: string }
  | { readonly phase: "EXPIRED" };

export type FlowEvent =
  | { readonly type: "CART_OPENED"; readonly cartId: string }
  | { readonly type: "CHECKOUT_STARTED"; readonly proposedKey: string }
  | { readonly type: "CHECKOUT_OK"; readonly orderId: string; readonly holdExpiresAt: number }
  | { readonly type: "CHECKOUT_REFUSED" }
  | { readonly type: "PAYMENT_FAILED"; readonly at: number }
  | { readonly type: "PAYMENT_AUTHORIZED" }
  | { readonly type: "PAYMENT_CONFIRMED" }
  | { readonly type: "TICK"; readonly at: number }
  | { readonly type: "RESTARTED" }
  /** B31h: back to the catalog with the cart surviving (session keeps the cartId). */
  | { readonly type: "SHOPPING_CONTINUED" }
  /**
   * B415: CLEAR CART empties everything and returns to BROWSE. From
   * PENDING_PAYMENT the App abandons the order FIRST (order.abandon —
   * holds free NOW, not at expiry) and dispatches this only on success.
   * An authorized order cannot be cleared: the money is in flight.
   */
  | { readonly type: "CART_CLEARED" };

export function initialFlow(): FlowState {
  return { phase: "BROWSE" };
}

function illegal(state: FlowState, event: FlowEvent): never {
  throw new Error(`illegal flow transition: ${event.type} in ${state.phase}`);
}

export function reduceFlow(state: FlowState, event: FlowEvent): FlowState {
  // §7 step 5 wins every race: nothing un-confirms a purchase in the UI.
  if (state.phase === "CONFIRMED") return state;

  switch (event.type) {
    case "CART_OPENED":
      if (state.phase === "BROWSE" || state.phase === "CART") return { phase: "CART", cartId: event.cartId };
      return illegal(state, event);

    case "CHECKOUT_STARTED":
      if (state.phase === "CART") return { phase: "CHECKOUT_IN_FLIGHT", cartId: state.cartId, checkoutKey: event.proposedKey };
      // Double tap: the first key stands — same key, one order (Inv 4).
      if (state.phase === "CHECKOUT_IN_FLIGHT") return state;
      return illegal(state, event);

    case "CHECKOUT_OK":
      if (state.phase === "CHECKOUT_IN_FLIGHT")
        return {
          phase: "PENDING_PAYMENT",
          cartId: state.cartId,
          checkoutKey: state.checkoutKey,
          orderId: event.orderId,
          holdExpiresAt: event.holdExpiresAt,
          authorized: false,
        };
      return illegal(state, event);

    case "CHECKOUT_REFUSED":
      // §7: provider outage degrades to "try again" without holds; carts survive.
      if (state.phase === "CHECKOUT_IN_FLIGHT") return { phase: "CART", cartId: state.cartId };
      return illegal(state, event);

    case "PAYMENT_FAILED":
      // §7: order stays PENDING_PAYMENT for retry until hold expiry.
      // Once authorized the Pay button no longer exists — a failure here
      // has no source, so it is a wall, not a race to absorb.
      if (state.phase === "PENDING_PAYMENT" && !state.authorized)
        return event.at < state.holdExpiresAt ? state : { phase: "EXPIRED" };
      return illegal(state, event);

    case "PAYMENT_AUTHORIZED":
      // Idempotent: the same fact twice (a re-resolved confirm) changes nothing.
      if (state.phase === "PENDING_PAYMENT") return state.authorized ? state : { ...state, authorized: true };
      return illegal(state, event);

    case "PAYMENT_CONFIRMED":
      if (state.phase === "PENDING_PAYMENT") return { phase: "CONFIRMED", orderId: state.orderId };
      return illegal(state, event);

    case "TICK":
      // The clock is ambient in every phase; it only moves a pending order —
      // and never an authorized one (B29: SUCCEEDED intent defers expiry).
      if (state.phase === "PENDING_PAYMENT" && !state.authorized && event.at >= state.holdExpiresAt)
        return { phase: "EXPIRED" };
      return state;

    case "RESTARTED":
      if (state.phase === "EXPIRED") return { phase: "BROWSE" };
      return illegal(state, event);

    case "CART_CLEARED":
      if (state.phase === "CART") return { phase: "BROWSE" };
      if (state.phase === "PENDING_PAYMENT" && !state.authorized) return { phase: "BROWSE" };
      return illegal(state, event);

    case "SHOPPING_CONTINUED":
      // B31h multi-line carts: browsing on is a CART affair only — the cart
      // survives in the session; CART_OPENED brings it back.
      if (state.phase === "CART") return { phase: "BROWSE" };
      return illegal(state, event);
  }
}
